import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment";
import { GetPolicyWalletAdminTopups, getReceiptImage } from "../../Service/_index";
import { debounce } from "../../Shared/debounce";
import NoData from "../../Resources/Images/NoDataNavy.png";
import ImageViewerDialog from "../UserWallet/components/ImageViewerDialog";
import UserAutocomplete from "./UserAutocomplete";
import { formatINR, isPdfKey, paymentModeLabel, proofKeyOf, proofNameOf } from "./addMoneyUtils";
import { FieldLabel, inputSx, outlinedButtonSx } from "./parts";
import Styles from "./AddMoneyToUser.module.scss";

const COLUMNS = ["Date", "User", "Amount", "Balance after", "Mode", "Reference", "Remarks", "Added by", "Proof", "Status"];

const STATUS_CHIPS = {
  COMPLETED: { label: "Completed", color: "#027A48", bg: "#ECFDF3", border: "#ABEFC6" },
  PENDING: { label: "Processing", color: "#B54708", bg: "#FFFAEB", border: "#FEDF89" },
  FAILED: { label: "Failed — not credited", color: "#B42318", bg: "#FEF3F2", border: "#FECDCA" },
};

const StatusChip = ({ status }) => {
  const tone = STATUS_CHIPS[status] || { label: status || "—", color: "#344054", bg: "#F2F4F7", border: "#D0D5DD" };
  return (
    <Chip
      size="small"
      label={tone.label}
      sx={{
        height: 22,
        fontFamily: "Inter",
        fontSize: "11.5px",
        fontWeight: 500,
        color: tone.color,
        backgroundColor: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    />
  );
};

const Dash = () => <span style={{ color: "#98A2B3" }}>—</span>;

const Ellipsis = ({ text, width }) =>
  text ? (
    <Tooltip title={text} arrow>
      <Box sx={{ maxWidth: width, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{text}</Box>
    </Tooltip>
  ) : (
    <Dash />
  );

const SkeletonRows = () =>
  Array.from({ length: 5 }).map((_, row) => (
    <TableRow key={row}>
      {COLUMNS.map((label) => (
        <TableCell key={label} className={Styles.bodyCell}>
          <Skeleton variant="text" width="80%" />
        </TableCell>
      ))}
    </TableRow>
  ));

const RecentAdditions = ({ users, usersLoading, refreshKey }) => {
  const [filters, setFilters] = useState({ userId: "", search: "" });
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [reloadKey, setReloadKey] = useState(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");

  const [proofLoadingId, setProofLoadingId] = useState("");
  const [viewer, setViewer] = useState({ open: false, imageUrl: "", fileName: "" });

  const latestRequest = useRef(0);

  const commitSearch = useMemo(
    () =>
      debounce((value) => {
        const search = value.trim();
        setFilters((prev) => (prev.search === search ? prev : { ...prev, search }));
        setPage(0);
      }, 400),
    []
  );

  const params = useMemo(() => {
    const query = { page, limit };
    if (filters.userId) query.userId = filters.userId;
    if (filters.search) query.search = filters.search;
    return query;
  }, [filters, page, limit]);

  useEffect(() => {
    const id = ++latestRequest.current;
    setLoading(true);
    setError("");
    GetPolicyWalletAdminTopups(params).then((res) => {
      if (id !== latestRequest.current) return; // a newer request superseded this one
      setLoading(false);
      if (res.error) {
        setError(res.message || "Could not load recent additions");
        return;
      }
      setRows(Array.isArray(res.data) ? res.data : []);
      setTotal(Number(res.total) || 0);
      setHasLoaded(true);
    });
  }, [params, reloadKey]);

  // A new credit was just made: jump back to page 1 where it will appear.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setPage(0);
    setReloadKey((n) => n + 1);
  }, [refreshKey]);

  const selectedUser = users.find((user) => user._id === filters.userId) || null;
  const filtered = Boolean(filters.userId || filters.search);

  const clearFilters = () => {
    setSearchInput("");
    setFilters({ userId: "", search: "" });
    setPage(0);
  };

  const viewProof = async (row) => {
    const key = proofKeyOf(row.receiptImage);
    if (!key || proofLoadingId) return;
    setProofLoadingId(row._id);
    const res = await getReceiptImage(key); // toasts its own failure
    setProofLoadingId("");
    if (!res || res.error || !res.url) return;
    // The image viewer cannot render a PDF, so let the browser open it.
    if (isPdfKey(key)) {
      window.open(res.url, "_blank", "noopener,noreferrer");
    } else {
      setViewer({ open: true, imageUrl: res.url, fileName: proofNameOf(row.receiptImage) });
    }
  };

  const showSkeleton = !hasLoaded && loading;
  const showEmpty = !error && hasLoaded && !loading && rows.length === 0;

  return (
    <>
      <Box className={Styles.SectionHead}>
        <Typography sx={{ color: "#101828", fontWeight: 500, fontSize: "16px", fontFamily: "Inter" }}>
          Recent additions
        </Typography>
        <Typography sx={{ color: "#667085", fontSize: "12.5px", fontFamily: "Inter" }}>
          Money you have added to user wallets
        </Typography>
        <Box
          sx={{
            mt: 1.5,
            display: "grid",
            gap: 1.5,
            alignItems: "end",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "minmax(0, 4fr) minmax(0, 4fr) auto" },
          }}
        >
          <Box>
            <FieldLabel htmlFor="recent-additions-user">User</FieldLabel>
            <UserAutocomplete
              id="recent-additions-user"
              value={selectedUser}
              onChange={(user) => {
                setFilters((prev) => ({ ...prev, userId: user?._id || "" }));
                setPage(0);
              }}
              options={users}
              loading={usersLoading}
              placeholder="All users"
            />
          </Box>
          <Box>
            <FieldLabel htmlFor="recent-additions-search">Search</FieldLabel>
            <TextField
              id="recent-additions-search"
              fullWidth
              size="small"
              value={searchInput}
              placeholder="Reference, remarks or user"
              onChange={(event) => {
                setSearchInput(event.target.value);
                commitSearch(event.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: "#667085" }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {filtered && (
              <Button variant="outlined" onClick={clearFilters} sx={outlinedButtonSx}>
                Clear filters
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <RefreshIcon />}
              onClick={() => setReloadKey((n) => n + 1)}
              disabled={loading}
              sx={outlinedButtonSx}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      {error && (
        <Box sx={{ px: 2.25, pb: 2 }}>
          <Box
            role="alert"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              border: "1px solid #f3d6d6",
              borderRadius: "8px",
              backgroundColor: "#fdf7f7",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 20, color: "#b3261e" }} />
            <Typography sx={{ flex: 1, fontFamily: "Inter", fontSize: "12.5px", color: "#b3261e" }}>{error}</Typography>
            <Button size="small" variant="outlined" onClick={() => setReloadKey((n) => n + 1)} sx={outlinedButtonSx}>
              Retry
            </Button>
          </Box>
        </Box>
      )}

      {showEmpty && (
        <Box className={Styles.EmptyState}>
          <img src={NoData} alt="" style={{ width: 240, height: 160, objectFit: "contain" }} />
          <Typography sx={{ color: "#1E3A5F", fontFamily: "Inter", fontSize: "18px", fontWeight: 600 }}>
            {filtered ? "No additions match these filters" : "No money added yet"}
          </Typography>
          <Typography sx={{ color: "#667085", fontFamily: "Inter", fontSize: "12.5px" }}>
            {filtered
              ? "Try another user or search term."
              : "Top-ups you make from this page will be listed here."}
          </Typography>
          {filtered && (
            <Button variant="outlined" onClick={clearFilters} sx={{ ...outlinedButtonSx, mt: 1 }}>
              Clear filters
            </Button>
          )}
        </Box>
      )}

      {!error && !showEmpty && (
        <Box className={Styles.tableWrap}>
          <Box sx={{ height: 3 }}>{loading && !showSkeleton && <LinearProgress sx={{ height: 3 }} />}</Box>
          <Box className={Styles.tableScroll}>
            <Table aria-label="Recent additions" sx={{ minWidth: 1180 }}>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((label) => (
                    <TableCell key={label} className={Styles.headCell}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody className={loading && !showSkeleton ? Styles.dim : ""}>
                {showSkeleton ? (
                  <SkeletonRows />
                ) : (
                  rows.map((row) => {
                    const proofKey = proofKeyOf(row.receiptImage);
                    return (
                      <TableRow key={row._id} className={Styles.row}>
                        <TableCell className={Styles.bodyCell} sx={{ whiteSpace: "nowrap" }}>
                          {row.createdAt ? moment(row.createdAt).format("DD/MM/YYYY hh:mm A") : <Dash />}
                        </TableCell>
                        <TableCell className={Styles.bodyCell} sx={{ fontWeight: 500 }}>
                          <Ellipsis text={row.userName} width={180} />
                        </TableCell>
                        <TableCell
                          className={Styles.bodyCell}
                          sx={{
                            whiteSpace: "nowrap",
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                            color: row.status === "FAILED" ? "#98A2B3 !important" : "#12762D !important",
                          }}
                        >
                          {formatINR(row.amount)}
                        </TableCell>
                        <TableCell className={Styles.bodyCell} sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                          {/* balanceAfter stays null until the top-up completes */}
                          {row.balanceAfter === null || row.balanceAfter === undefined ? (
                            <Dash />
                          ) : (
                            formatINR(row.balanceAfter)
                          )}
                        </TableCell>
                        <TableCell className={Styles.bodyCell}>
                          {row.paymentMode ? (
                            <Chip
                              size="small"
                              label={paymentModeLabel(row.paymentMode)}
                              sx={{
                                height: 22,
                                fontFamily: "Inter",
                                fontSize: "11.5px",
                                color: "#1E3A5F",
                                backgroundColor: "#EEF3F9",
                              }}
                            />
                          ) : (
                            <Dash />
                          )}
                        </TableCell>
                        <TableCell className={Styles.bodyCell}>
                          <Ellipsis text={row.reference} width={140} />
                        </TableCell>
                        <TableCell className={Styles.bodyCell}>
                          <Ellipsis text={row.remarks} width={200} />
                        </TableCell>
                        <TableCell className={Styles.bodyCell}>
                          <Ellipsis text={row.addedByName} width={140} />
                        </TableCell>
                        <TableCell className={Styles.bodyCell}>
                          {proofKey ? (
                            <Tooltip title="View proof">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => viewProof(row)}
                                  disabled={Boolean(proofLoadingId)}
                                  aria-label={`View proof for ${row.userName || "this top-up"}`}
                                  sx={{ color: "#1E3A5F" }}
                                >
                                  {proofLoadingId === row._id ? (
                                    <CircularProgress size={18} sx={{ color: "#1E3A5F" }} />
                                  ) : (
                                    <VisibilityIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Dash />
                          )}
                        </TableCell>
                        <TableCell className={Styles.bodyCell}>
                          <StatusChip status={row.status} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
          <Box className={Styles.pagination}>
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50]}
              count={total}
              rowsPerPage={limit}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              onRowsPerPageChange={(event) => {
                setLimit(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Box>
        </Box>
      )}

      <ImageViewerDialog
        open={viewer.open}
        onClose={() => setViewer({ open: false, imageUrl: "", fileName: "" })}
        imageUrl={viewer.imageUrl}
        fileName={viewer.fileName}
      />
    </>
  );
};

export default RecentAdditions;
